import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Bar } from '../../models/bar.model';

@Component({
  selector: 'app-bar-entry',
  imports: [CommonModule],
  templateUrl: './bar-entry.html',
  styleUrl: './bar-entry.scss',
})
export class BarEntry {
  @Input() bar: Bar | null = null;
  @Output() seeDetail = new EventEmitter<string>();

  constructor(private router: Router) {}
  private daysOfWeek = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

  get listTypeClass(): string {
    if (!this.bar?.type) return 'unknown';
    return this.bar.type.toLowerCase().replace(/\s+/g, '-');
  }

  private parseTimeRange(timeStr: string): { start: string; end: string } | null {
    // Busca formato "HH:MM–HH:MM" o "HH:MM-HH:MM" (con guion largo o corto)
    const match = timeStr.match(/(\d{1,2}):(\d{2})[–\-](\d{1,2}):(\d{2})/);
    if (!match) return null;
    
    const start = `${String(match[1]).padStart(2, '0')}:${match[2]}`;
    const end = `${String(match[3]).padStart(2, '0')}:${match[4]}`;
    return { start, end };
  }

  private isOpenInRange(currentHour: string, timeRanges: string): boolean {
    // Busca todos los rangos (separados por comas)
    const ranges = timeRanges.split(',');
    
    for (const range of ranges) {
      const parsed = this.parseTimeRange(range.trim());
      if (parsed) {
        // Manejo especial para horarios que cruzan medianoche (ej: 20:00–0:30)
        if (parsed.end < parsed.start) {
          // Horario que cruza medianoche
          if (currentHour >= parsed.start || currentHour < parsed.end) {
            return true;
          }
        } else {
          // Horario normal
          if (currentHour >= parsed.start && currentHour < parsed.end) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private getNextOpeningFromRange(currentHour: string, timeRanges: string): string | null {
    const ranges = timeRanges.split(',');
    
    for (const range of ranges) {
      const parsed = this.parseTimeRange(range.trim());
      if (parsed) {
        if (parsed.end >= parsed.start) {
          // Horario normal
          if (currentHour < parsed.start) {
            return parsed.start;
          }
        }
      }
    }
    return null;
  }

  get isOpenNow(): boolean {
    if (!this.bar?.regularOpeningHours || this.bar.regularOpeningHours.length === 0) return false;
    
    const now = new Date();
    const currentDay = this.daysOfWeek[now.getDay()];
    const currentHour = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    const todaySchedule = this.bar.regularOpeningHours.find(h => h.toLowerCase().includes(currentDay));
    if (!todaySchedule) return false;
    
    // Extrae la parte de horarios (después del "día: ")
    const scheduleMatch = todaySchedule.match(/:\s*(.+)$/);
    if (!scheduleMatch) return false;
    
    return this.isOpenInRange(currentHour, scheduleMatch[1]);
  }

  get nextOpeningTime(): string {
    if (!this.bar?.regularOpeningHours || this.bar.regularOpeningHours.length === 0) return 'Horario no disponible';
    
    const now = new Date();
    const currentDay = this.daysOfWeek[now.getDay()];
    const currentHour = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    // Buscar horario de hoy
    const todaySchedule = this.bar.regularOpeningHours.find(h => h.toLowerCase().includes(currentDay));
    if (todaySchedule) {
      const scheduleMatch = todaySchedule.match(/:\s*(.+)$/);
      if (scheduleMatch) {
        const timeRanges = scheduleMatch[1];
        
        // Si está abierto ahora
        if (this.isOpenInRange(currentHour, timeRanges)) {
          // Busca la hora de cierre
          const ranges = timeRanges.split(',');
          for (const range of ranges) {
            const parsed = this.parseTimeRange(range.trim());
            if (parsed) {
              if (parsed.end >= parsed.start) {
                if (currentHour >= parsed.start && currentHour < parsed.end) {
                  return `Abierto hasta las ${parsed.end}`;
                }
              } else {
                // Horario que cruza medianoche
                if (currentHour >= parsed.start || currentHour < parsed.end) {
                  return `Abierto hasta las ${parsed.end}`;
                }
              }
            }
          }
        }
        
        // Si no está abierto ahora, busca la próxima apertura hoy
        const nextTime = this.getNextOpeningFromRange(currentHour, timeRanges);
        if (nextTime && nextTime > currentHour) {
          return `Abre hoy a las ${nextTime}`;
        }
      }
    }
    
    // Buscar próximo día abierto
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(now);
      nextDate.setDate(now.getDate() + i);
      const nextDay = this.daysOfWeek[nextDate.getDay()];
      const schedule = this.bar.regularOpeningHours.find(h => h.toLowerCase().includes(nextDay));
      
      if (schedule) {
        const scheduleMatch = schedule.match(/:\s*(.+)$/);
        if (scheduleMatch) {
          const ranges = scheduleMatch[1].split(',');
          for (const range of ranges) {
            const parsed = this.parseTimeRange(range.trim());
            if (parsed) {
              return `Abre el ${nextDay} a las ${parsed.start}`;
            }
          }
        }
      }
    }
    
    return 'Horario no disponible';
  }

  get ratingColor(): string {
    if (!this.bar?.rating) return 'neutral';
    if (this.bar.rating >= 4.5) return 'excellent';
    if (this.bar.rating >= 4) return 'very-good';
    if (this.bar.rating >= 3.5) return 'good';
    if (this.bar.rating >= 3) return 'fair';
    return 'poor';
  }

  viewDetails() {
    if (this.bar?.id) {
      // Emitir evento para que el padre guarde el estado antes de navegar
      this.seeDetail.emit(this.bar.id);
      
      // Navegar después de emitir el evento
      setTimeout(() => {
        this.router.navigate(['/bar', this.bar!.id]);
      }, 0);
    }
  }
}
